(function(window, document, HTMLIFrameElement) {
    'use strict'

    /**
     * @namespace ParentToIframeCommunication
     * @description
     * The interface for sending messages from website to iframe.
     * 
     * <h4>Installation</h4>
     * <h5>Static pages (HTML, PHP)</h5>
     * Include JS scripts after iframe html element.<br />
     * Include library file after script tag with initialization of communication library.
     * <xmp class="p1 overflow-auto round fill-light" style="font-family: Source Code Pro,Menlo,Consolas,Liberation Mono,monospace;font-size: 14px;"><iframe id="myIframe" src="url/to/iframe?userId=12345&lang=en-EN"></iframe>
     * <script>
     *     //
     *     // Initialize library.
     *     //
     *     function initCommunication() {
     *         // Get iframe element and init communication library.
     *         const iframe = document.getElementById('myIframe').communication()
     *         
     *         // Event listeners examples.
     *         iframe
     *             .onAuthLoginMessage(() => {
     *                 ...
     *             })
     *             .onAuthRegisterMessage(() => {
     *                 ...
     *             })
     *             ...
     *     }
     * 
     * </script>
     * <script src="url/to/external-script/iframe-communication.min.js?time=[CURRENT_TIMESTAMP]" onload="initCommunication()" defer></script>
     * </xmp>
     * 
     * <h5>VUE.js</h5>
     * Add iframe element to the component template:
     * <xmp class="p1 overflow-auto round fill-light" style="font-family: Source Code Pro,Menlo,Consolas,Liberation Mono,monospace;font-size: 14px;"><template>
     * ...
     *     <div class="iframe">
     *         <iframe
     *             ref="tool-iframe"
     *             :src="url/to/iframe?userId=12345&lang=en-EN"
     *         ></iframe>
     *     </div>
     * ...
     * </template>
     * </xmp>
     * 
     * Create callback for script event listener:
     * <xmp class="p1 overflow-auto round fill-light" style="font-family: Source Code Pro,Menlo,Consolas,Liberation Mono,monospace;font-size: 14px;">methods: {
     * &nbsp;&nbsp;&nbsp;&nbsp;...
     * &nbsp;&nbsp;&nbsp;&nbsp;initCommunication() {
     * &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;// Get iframe element and init communication library.
     * &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;const iframe = this.$refs["tool-iframe"].communication()
     * &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
     * &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;// Event listeners examples.
     * &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;iframe
     * &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;.onAuthLoginMessage(() => {
     * &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;this.$router.push({ name: "LoginPage" })
     * &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;})
     * &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;.onAuthRegisterMessage(() => {
     * &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;this.$router.push({ name: "RegisterPage" })
     * &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;})
     * &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp&nbsp;&nbsp;&nbsp;&nbsp;...
     * &nbsp;&nbsp;&nbsp;&nbsp;}
     * &nbsp;&nbsp;&nbsp;&nbsp;...
     * }
     * </xmp>
     * 
     * Add script to mounted lifecycle hook:
     * <xmp class="p1 overflow-auto round fill-light" style="font-family: Source Code Pro,Menlo,Consolas,Liberation Mono,monospace;font-size: 14px;">mounted() {
     * &nbsp;&nbsp;&nbsp;&nbsp;...
     * &nbsp;&nbsp;&nbsp;&nbsp;const communicationScript = document.createElement("script")
     * &nbsp;
     * &nbsp;&nbsp;&nbsp;&nbsp;communicationScript.setAttribute("src", "url/to/external-script/iframe-communication.min.js?time=[CURRENT_TIMESTAMP]")
     * &nbsp;&nbsp;&nbsp;&nbsp;communicationScript.addEventListener("load", this.initCommunication)
     * &nbsp;
     * &nbsp;&nbsp;&nbsp;&nbsp;this.$el.appendChild(communicationScript)
     * &nbsp;&nbsp;&nbsp;&nbsp;...
     * }
     * </xmp>
     * 
     * Add destroy method to beforeUnmount hook:
     * <xmp class="p1 overflow-auto round fill-light" style="font-family: Source Code Pro,Menlo,Consolas,Liberation Mono,monospace;font-size: 14px;">beforeUnmount() {
     * &nbsp;&nbsp;&nbsp;&nbsp;...
     * &nbsp;&nbsp;&nbsp;&nbsp;this.$refs["tool-iframe"].destroyCommunication()
     * &nbsp;&nbsp;&nbsp;&nbsp;...
     * }
     * </xmp>
     * 
     * <h4>Information</h4>
     * The interface has some built-in messages types that are fired automatically:
     * 
     * - "init" - Fire when iframe element was loaded.
     * - "resize-width" - Fire when window width change.
     * - "scroll" - Fire when scroll position change.
     * <br />
     * 
     * <div class="py1 quiet mt1 prose-big">Interface</div>
     * @example
     * ...
     *     <iframe id="myIframe" src="url/to/?userId=12345&lang=en-EN"></iframe>
     *     <script>
     *         //
     *         // Initialize library.
     *         //
     *         function initCommunication() {
     *             // Get iframe element and init communication library.
     *             const iframe = document.getElementById('myIframe').communication()
     * 
     *             //
     *             // Event listeners.
     *             // Examples.
     *             //
     * 
     *             // Listen all messages from iframe.
     *             iframe.onMessages(function (data, type) {
     *                 ...
     *             })
     * 
     *             // Listen custom message from iframe.
     *             iframe.onMessage('message-type-name', function (data, type) {
     *                 ...
     *             })
     * 
     *             // Listen specific message from iframe.
     *             iframe.onAuthLoginMessage(function (data, type) {
     *                 ...
     *             })
     * 
     *            // Send custom message to iframe.
     *            iframe.sendMessage('message-type-name', {
     *                key: 'value',
     *                ...
     *            })
     * 
     *            ...
     *         }
     *     </script>
     *     <script src="url/to/external-script/iframe-communication.min.js?time=[CURRENT_TIMESTAMP]" onload="initCommunication()" defer></script>
     *     ...
     * </body>
     * </html>
     */
    HTMLIFrameElement.prototype.communication = function (config) {
        var iframe = this

        /*
         * The config object.
         * 
         * @param  {object}  config
         */
        config = Object.assign({}, config)

        /*
         * The registered message event listeners.
         *
         * @var {array}
         */
        var registeredMessageEventListeners = []

        /**
         * Send custom message to iframe.
         * 
         * @param  {string}  type  The name of message type.
         * @param  {object}  data  The message data.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Send custom message to iframe.
         * iframe.sendMessage('message-type-name', {
         *     key: 'value',
         *     ...
         * })
         */
        iframe.sendMessage = function (type, data) {
            var origin = iframe.src.match(/(^[^\/]*\/\/[^\/]*)/g)
            
            iframe.contentWindow.postMessage({
                type: type,
                data: data
            }, '*')

            return iframe
        }

        /**
         * Send resize width message to iframe.
         * <p style="font-size: 80%; color: red;">This method is performed automatically each time the browser window size changes.</p>
         * 
         * @param  {string}  [type]  The name of message type (optional).
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Send resize-width message to iframe.
         * iframe.sendResizeMessage()
         */
        iframe.sendResizeMessage = function (type) {
            type = type || 'resize-width'

            iframe.sendMessage(type, {
                width: window.innerWidth,
                iframeOffsetTop: iframe.offsetTop,
                iframeScrollTop: window.scrollY - iframe.offsetTop,
                parentScrollTop: window.scrollY
            })

            return iframe
        }

        /**
         * Send scroll message to iframe.
         * <p style="font-size: 80%; color: red;">This method is performed automatically each time the browser window scroll changes.</p>
         * 
         * @param  {string}  [type]  The name of message type (optional).
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Send resize-width message to iframe.
         * iframe.sendScrollMessage()
         */
        iframe.sendScrollMessage = function (type) {
            type = type || 'scroll'

            iframe.sendMessage(type, {
                iframeOffsetTop: iframe.getBoundingClientRect().top + window.scrollY,
                iframeScrollTop: iframe.getBoundingClientRect().top * -1,
                parentScrollTop: window.scrollY
            })

            return iframe
        }

        /**
         * Listen custom message from iframe.
         * 
         * @param  {string}  type  The name of message type.
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen custom message from iframe.
         * iframe.onMessage('message-type-name', function (data, type) {
         *     ...
         * })
         */
        iframe.onMessage = function (type, callback) {
            var messageEventListener = function (event) {
                if ((!type && typeof event.data.type == 'undefined') || (type && event.data.type != type))
                    return

                callback(event.data.data, event.data.type)
            }

            window.addEventListener("message", messageEventListener, false)
            registeredMessageEventListeners.push(messageEventListener)

            return iframe
        }

        /**
         * Listen all messages from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen all messages from iframe.
         * iframe.onMessages(function (data, type) {
         *     ...
         * })
         */
        iframe.onMessages = function (callback) {
            iframe.onMessage(null, callback)

            return iframe
        }

        /**
         * Listen ready message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen ready message from iframe.
         * iframe.onReadyMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onReadyMessage = function (callback) {
            iframe.onMessage('ready', callback)

            return iframe
        }

        /**
         * Listen scroll to message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen scroll-to message from iframe.
         * iframe.onScrollToMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onScrollToMessage = function (callback) {
            iframe.onMessage('scroll-to', callback)

            return iframe
        }

        /**
         * Listen auth login message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen auth login message from iframe.
         * iframe.onAuthLoginMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onAuthLoginMessage = function (callback) {
            var messageEventListener = function (event) {
                if (typeof event.data.type == 'undefined' || event.data.type != 'redirect' || typeof event.data.data.redirect == 'undefined' || event.data.data.redirect !== 'login')
                    return

                callback(event.data.data, event.data.type)
            }

            window.addEventListener("message", messageEventListener, false)
            registeredMessageEventListeners.push(messageEventListener)

            return iframe
        }

        /**
         * Listen auth register message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen auth register message from iframe.
         * iframe.onAuthRegisterMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onAuthRegisterMessage = function (callback) {
            var messageEventListener =  function (event) {
                if (typeof event.data.type == 'undefined' || event.data.type != 'redirect' || typeof event.data.data.redirect == 'undefined' || event.data.data.redirect !== 'register')
                    return

                callback(event.data.data, event.data.type)
            }

            window.addEventListener("message", messageEventListener, false)
            registeredMessageEventListeners.push(messageEventListener)

            return iframe
        }

        /**
         * Listen user action message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen user action message from iframe.
         * iframe.onUserActionMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onUserActionMessage = function (callback) {
            iframe.onMessage('user-action', callback)

            return iframe
        }

        /**
         * Listen start new calculation action message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen start new calculation action message from iframe.
         * iframe.onStartNewCalculationMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onStartNewCalculationMessage = function (callback) {
            iframe.onMessage('user-action', function (data, type) {
                if (typeof data.action == 'undefined' || data.action != 'start new calculation')
                    return

                callback(data, type)
            })

            return iframe
        }

        /**
         * Listen load calculation action message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen load calculation action message from iframe.
         * iframe.onLoadCalculationMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onLoadCalculationMessage = function (callback) {
            iframe.onMessage('user-action', function (data, type) {
                if (typeof data.action == 'undefined' || data.action != 'load calculation')
                    return

                callback(data, type)
            })

            return iframe
        }

        /**
         * Listen cancel calculation action message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen cancel calculation action message from iframe.
         * iframe.onCancelCalculationMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onCancelCalculationMessage = function (callback) {
            iframe.onMessage('user-action', function (data, type) {
                if (typeof data.action == 'undefined' || data.action != 'cancel calculation')
                    return

                callback(data, type)
            })

            return iframe
        }

        /**
         * Listen finish calculation action message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen finish calculation action message from iframe.
         * iframe.onFinishCalculationMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onFinishCalculationMessage = function (callback) {
            iframe.onMessage('user-action', function (data, type) {
                if (typeof data.action == 'undefined' || data.action != 'finish calculation')
                    return

                callback(data, type)
            })

            return iframe
        }

        /**
         * Listen save calculation action message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen save calculation action message from iframe.
         * iframe.onSaveCalculationMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onSaveCalculationMessage = function (callback) {
            iframe.onMessage('user-action', function (data, type) {
                if (typeof data.action == 'undefined' || data.action != 'save calculation')
                    return

                callback(data, type)
            })

            return iframe
        }

        /**
         * Listen share calculation action message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen share calculation action message from iframe.
         * iframe.onShareCalculationMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onShareCalculationMessage = function (callback) {
            iframe.onMessage('user-action', function (data, type) {
                if (typeof data.action == 'undefined' || data.action != 'share calculation')
                    return

                callback(data, type)
            })

            return iframe
        }

        /**
         * Listen send calculation action message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen send calculation action message from iframe.
         * iframe.onSendCalculationResultsMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onSendCalculationResultsMessage = function (callback) {
            iframe.onMessage('user-action', function (data, type) {
                if (typeof data.action == 'undefined' || data.action != 'send calculation results')
                    return

                callback(data, type)
            })

            return iframe
        }

        /**
         * Listen donwload calculation action message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen donwload calculation action message from iframe.
         * iframe.onDownloadCalculationResultsMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onDownloadCalculationResultsMessage = function (callback) {
            iframe.onMessage('user-action', function (data, type) {
                if (typeof data.action == 'undefined' || data.action != 'download calculation results')
                    return

                callback(data, type)
            })

            return iframe
        }

        /**
         * Listen send email action message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen send email action message from iframe.
         * iframe.onSendEmailMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onSendEmailMessage = function (callback) {
            iframe.onMessage('email', function (data, type) {
                callback(data, type)
            })

            return iframe
        }

        /**
         * Listen send quotation action message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen send quotation action message from iframe.
         * iframe.onSendQuotationMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onSendQuotationMessage = function (callback) {
            iframe.onMessage('quotation', function (data, type) {
                callback(data, type)
            })

            return iframe
        }

        /**
         * Listen change step action message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen change step action message from iframe.
         * iframe.onChangeStepMessage(function (data, type) {
         *     ...
         * })
         */
        iframe.onChangeStepMessage = function (callback) {
            iframe.onMessage('user-action', function (data, type) {
                if (typeof data.action == 'undefined' || data.action != 'change step' || data.action != 'previous step' || data.action != 'next step')
                    return

                callback(data, type)
            })

            return iframe
        }

        /**
         * Listen get cookie message from iframe.
         * 
         * @param  {function (value: object, name: string, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen get cookie action message from iframe.
         * iframe.onGetCookie(function (value, name, type) {
         *     ...
         * })
         */
        iframe.onGetCookie = function (callback) {
            iframe.onMessage('get-cookie', function (data, type) {
                var result = document.cookie.match(new RegExp('_communication_'+ data.calculationTool +'_'+ data.name + '=([^;]+)'))
                result && (result = JSON.parse(result[1]))
                result = result
                    ? {
                        name: data.name,
                        value: result,
                        calculationTool: data.calculationTool
                    }
                    : undefined
                
                iframe.sendMessage('send-cookie', result)
                
                if (typeof callback != 'undefined')
                    callback(
                        result ? result.value : undefined,
                        result ? result.name : undefined,
                        data.calculationTool,
                        type
                    )
            })

            return iframe
        }
        
        /**
         * Listen set cookie message from iframe.
         * 
         * @param  {function (value: object, name: string, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen get cookie action message from iframe.
         * iframe.onSetCookie(function (value, name, calculationTool, type) {
         *     ...
         * })
         */
        iframe.onSetCookie = function (callback) {
            iframe.onMessage('set-cookie', function (data, type) {
                callback(data.value, data.name, data.calculationTool, type)
            })

            return iframe
        }

        /**
         * Listen delete cookie message from iframe.
         * 
         * @param  {function (value: object, name: string, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen get cookie action message from iframe.
         * iframe.onDeleteCookie(function (name, calculationTool, type) {
         *     ...
         * })
         */
        iframe.onDeleteCookie = function (callback) {
            iframe.onMessage('delete-cookie', function (data, type) {
                callback(data.name, data.calculationTool, type)
            })

            return iframe
        }

        /**
         * Listen get storage message from iframe.
         * 
         * @param  {function (value: object, name: string, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen get storage action message from iframe.
         * iframe.onGetStorage(function (value, name, type) {
         *     ...
         * })
         */
         iframe.onGetStorage = function (callback) {
            iframe.onMessage('get-storage', function (data, type) {
                var key = '_communication_'+ data.calculationTool +'_'+ data.name,
                    result = localStorage.getItem(key)

                result && (result = JSON.parse(result))
                result = result
                    ? {
                        name: data.name,
                        value: result,
                        calculationTool: data.calculationTool
                    }
                    : undefined
                
                iframe.sendMessage('send-storage', result)
                
                if (typeof callback != 'undefined')
                    callback(
                        result ? result.value : undefined,
                        result ? result.name : undefined,
                        data.calculationTool,
                        type
                    )
            })

            return iframe
        }
        
        /**
         * Listen set storage message from iframe.
         * 
         * @param  {function (value: object, name: string, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen get storage action message from iframe.
         * iframe.onSetStorage(function (value, name, calculationTool, type) {
         *     ...
         * })
         */
        iframe.onSetStorage = function (callback) {
            iframe.onMessage('set-storage', function (data, type) {
                callback(data.value, data.name, data.calculationTool, type)
            })

            return iframe
        }

        /**
         * Listen delete storage message from iframe.
         * 
         * @param  {function (value: object, name: string, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen get storage action message from iframe.
         * iframe.onDeleteStorage(function (name, calculationTool, type) {
         *     ...
         * })
         */
        iframe.onDeleteStorage = function (callback) {
            iframe.onMessage('delete-storage', function (data, type) {
                callback(data.name, data.calculationTool, type)
            })

            return iframe
        }

        /**
         * Listen get offset message from iframe.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Listen get offset action message from iframe.
         * iframe.onGetOffset(function (data, type) {
         *     ...
         * })
         */
        iframe.onGetOffset = function (callback) {
            iframe.onMessage('get-offset', function (data, type) {
                var result = {
                    iframeOffsetTop: iframe.getBoundingClientRect().top + window.scrollY,
                    iframeScrollTop: iframe.getBoundingClientRect().top * -1,
                    parentScrollTop: window.scrollY,
                    viewportHeight: window.innerHeight
                }
                
                iframe.sendMessage('send-offset', result)
                
                if (typeof callback != 'undefined')
                    callback(result, type)
            })

            return iframe
        }

        /**
         * Destroy communication library and remove all event listeners.
         * 
         * @return {void}
         * 
         * @memberof ParentToIframeCommunication.prototype
         * @example
         * // Destroy communication.
         * iframe.destroyCommunication()
         */
        iframe.destroyCommunication = function () {
            registeredMessageEventListeners.forEach(function (item) {
                window.removeEventListener('message', item)
            })
        }

        /*
         * Listen iframe on load event.
         */
        iframe.addEventListener('load', function () {
            iframe.sendResizeMessage('init')
        }, false)

        /*
         * Listen event "ready" from iframe.
         */
        iframe.onMessage('ready', function (data) {
            iframe.height = data.height
            iframe.classList.add('loaded')
            iframe.classList.remove('loading')
        })

        /*
         * Listen event "resize-height" from iframe.
         */
        iframe.onMessage('resize-height', function (data) {
            iframe.height = data.height
        })

        /*
         * Init iframe style.
         */
        iframe.style.overflow = 'hidden'

        /*
         * Listen event "scroll-to" from iframe.
         */
        iframe.onScrollToMessage(function (data) {
            var offsetTop = parseInt(iframe.getBoundingClientRect().top  + window.scrollY + data.position)
            window.scrollTo(0, offsetTop)
        })

        /*
         * Listen event "get-cookie" from iframe.
         */
        iframe.onGetCookie()

        /*
         * Listen event "set-cookie" from iframe.
         */
        iframe.onSetCookie(function (value, name, calculationTool, type) {
            var cookie = ['_communication_'+ calculationTool +'_'+ name, '=', JSON.stringify(value), ';path=/;'].join('')
            document.cookie = cookie
        })

        /*
         * Listen event "delete-cookie" from iframe.
         */
        iframe.onDeleteCookie(function (name, calculationTool, type) {
            var cookie = ['_communication_'+ calculationTool +'_'+ name, '=', ';path=/;Expires=Thu, 01 Jan 1970 00:00:01 GMT;'].join('')
            document.cookie = cookie
        })

        /*
         * Listen event "get-storage" from iframe.
         */
        iframe.onGetStorage()

        /*
         * Listen event "set-storage" from iframe.
         */
        iframe.onSetStorage(function (value, name, calculationTool, type) {
            var key = '_communication_'+ calculationTool +'_'+ name

            window.localStorage.setItem(key, JSON.stringify(value));
        })

        /*
         * Listen event "delete-storage" from iframe.
         */
        iframe.onDeleteStorage(function (name, calculationTool, type) {
            var key = '_communication_'+ calculationTool +'_'+ name

            window.localStorage.removeItem(key);
        })

        /*
         * Listen event "get-offset" from iframe.
         */
        iframe.onGetOffset()

        /*
         * Listen window on resize event.
         */
        var resizeEventListener = function () {
            iframe.sendScrollMessage()
        }
        window.addEventListener('resize', resizeEventListener, false)
        registeredMessageEventListeners.push(resizeEventListener)

        /*
         * Listen window on scroll event.
         */
        var scrollEventListener = function () {
            iframe.sendScrollMessage()
        }
        window.addEventListener("scroll", scrollEventListener, false)
        registeredMessageEventListeners.push(scrollEventListener)

        return iframe
    }

    /**
     * @namespace IframeToParentCommunication
     * @return {instance}
     * @description
     * The interface for sending messages from iframe to a parent.
     * 
     * <h4>Installation</h4>
     * Include library file on iframe document.
     * <xmp class="p1 overflow-auto round fill-light" style="font-family: Source Code Pro,Menlo,Consolas,Liberation Mono,monospace;font-size: 14px;"><script src="path/to/script/iframe.communication.min.js"></script>
     * </xmp>
     * 
     * <h4>Initialization</h4>
     * Include JS scripts after library include script tag.
     * <br />Set config param "calculationTool", "version" and "build".
     * <xmp class="p1 overflow-auto round fill-light" style="font-family: Source Code Pro,Menlo,Consolas,Liberation Mono,monospace;font-size: 14px;"><script>
     * // Initialization
     * communication.config({
     *     calculationTool: 'calculator-name',
     *     version: 'calculator-version',
     *     build: 'calculator-build-number'
     * })
     * ...
     * </xmp>
     * 
     * <h4>Information</h4>
     * The interface for sending messages from iframe to a parent.
     * <br /><br />The interface has some built-in messages types that are fired automatically:
     * 
     * - "ready" - Fire when iframe element DOM is fully loaded.
     * - "resize-height" - Fire when iframe element height change.
     * <br /><br />
     * 
     * The interface allows to reading and saving information in a cookie file, which is stored in the parent window.
     * - Get cookie by name
     * <pre class="p1 overflow-auto round fill-light">
     * communication.getCookie('cookie-name', function (value, name, type) {
     *     ...
     * })
     * - Set cookie value
     * <pre class="p1 overflow-auto round fill-light">
     * communication.setCookie('cookie-name', {
     *     key: 'value',
     *     ...
     * })
     * </pre>
     * 
     * The interface allows to reading and saving information in a browser local storage, which is stored in the parent window.
     * - Get storage by name
     * <pre class="p1 overflow-auto round fill-light">
     * communication.getStorage('storage-name', function (value, name, type) {
     *     ...
     * })
     * - Set storage value
     * <pre class="p1 overflow-auto round fill-light">
     * communication.setStorage('storage-name', {
     *     key: 'value',
     *     ...
     * })
     * </pre>
     * 
     * <h4>Google Tag Manager</h4>
     * The interface can automatically send event to dataLayer.
     * 
     * - Installation
     * <p>Set config param "gtmId" to initialization method.</p>
     * <pre class="p1 overflow-auto round fill-light">
     * // Initialization
     * communication.config({
     *     ...
     *     gtmId: 'GTM-XXXX',
     *     ...
     * })
     * </pre>
     * - How it work?
     * <p>Event is automatically pushed to the data layer when the interface sends message to parent window.</p>
     * <pre class="p1 overflow-auto round fill-light">
     * dataLayer.push({
     *     event: 'tools_message_sent',
     *     tools_message_type: type,
     *     tools_message_data: data
     * })
     * </pre>
     * 
     * <div class="py1 quiet mt1 prose-big">Interface</div>
     * @example
     * ...
     *     <script src="path/to/script/iframe.communication.min.js"></script>
     *     <script>
     *         //
     *         // Examples
     *         //
     * 
     *         // Init 
     *         communication.config({
     *             calculationTool: 'calculator-name',
     *             version: 'calculator-version',
     *             build: 'calculator-build-number'
     *         })
     *         
     *         // Get iframe locations
     *             // Get iframe "href".
     *             var href = communication.getLocation('href')
     * 
     *             // Get iframe "origin".
     *             var origin = communication.getLocation('origin')
     * 
     *             ...
     * 
     *         // Get params
     *             // Get "userId" param.
     *             var userId = communication.getParam('userId')
     * 
     *             // Get "lang" param
     *             var lang = communication.getParam('lang')
     * 
     *             ...
     * 
     *         // Listen events
     *             // Listen all messages from parent.
     *             communication.onMessages(function (data, type) {
     *                 ...
     *             })
     * 
     *             // Listen custom message from parent.
     *             communication.onMessage('message-type-name', function (data, type) {
     *                 ...
     *             })
     * 
     *             // Listen specific message from parent.
     *             communication.onInitMessage(function (data, type) {
     *                 ...
     *             })
     * 
     *         // Send custom message to parent.
     *         communication.sendMessage('message-type-name', {
     *             key: 'value',
     *             ...
     *         })
     * 
     *         // Send specific message to parent.
     *         communication.sendAuthLoginMessage({
     *             key: 'value',
     *             ...
     *         })
     *     </script>
     * </body>
     * </html>
     */
    function communication(isIframe) {
        isIframe = isIframe || false;

        var self = {}

        /*
         * Get url params
         * 
         * @return {object}
         */
        var getUrlParams = function () {
            var urlParams = window.location.search.match(new RegExp(/(?:[\?\&])([^&]+)/g)),
                params = {}

            if (urlParams) {
                urlParams.forEach(function (param, index, arr) {
                    param = param.replace(/\?|\&/g, '').split('=')
                    params[param[0]] = param[1]
                })
            }

            return params
        }

        /*
         * Listener element height change.
         * 
         * @param  {DOMElement}  element
         * @param  {function}  callback
         * @return {void}
         */
        var onElementHeightChange = function (element, callback) {
            var lastHeight = element.clientHeight, newHeight
            
            (function run() {
                newHeight = element.clientHeight
                if (lastHeight != newHeight)
                    callback()

                lastHeight = newHeight
                if (element.onElementHeightChangeTimer)
                    clearTimeout(element.onElementHeightChangeTimer)
        
                element.onElementHeightChangeTimer = setTimeout(run)
            })()
        }

        /*
         * Init google tag manager script.
         * 
         * @return {void}
         */
        var initGoogleTagManager = function () {
            var gtmId = self.getParam('gtmId')
            gtmId = gtmId || self.appConfig.gtmId
            
            if (! gtmId)
                return

            var body = document.getElementsByTagName('body')[0],
                inlineScript = document.createElement('script'),
                noscript = document.createElement('noscript')

            inlineScript.innerHTML = "(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','"+ gtmId +"');"
            noscript.innerHTML = '<iframe src="https://www.googletagmanager.com/ns.html?id='+  gtmId +'" height="0" width="0" style="display:none;visibility:hidden"></iframe>'

            document.getElementsByTagName('head')[0].appendChild(inlineScript) 
            body.insertBefore(noscript, body.firstChild)
        }

        /*
         * Send google tag manager event.
         *
         * @param  {object}  data
         * @return {void}
         * */
        var sendGoogleTagManagerEvent = function (data) {
            var gtmId = self.getParam('gtmId') || self.appConfig.gtmId

            if (! gtmId || typeof dataLayer == 'undefined')
                return

            dataLayer.push(data)
        }

        /*
         * Default config. 
         */
        var defConfig = {
            "calculationTool": null,
            "gtmId": null
        }

        /*
         * The config object.
         * 
         * @param  {object}  appConfig
         */
        self.appConfig = Object.assign({}, defConfig)

        /**
         * The location object.
         * 
         * @param  {object}  location
         * @param  {string}  location.href  The iframe full url.
         * @param  {string}  location.hrefBase  The iframe base url.
         * @param  {string}  location.origin  The iframe origin url.
         * @param  {string}  location.search  The iframe url search.
         * @param  {object}  location.params  The iframe url params.
         * 
         * @memberof IframeToParentCommunication
         */
        self.location = {
            "href": window.location.href,
            "hrefBase": window.location.href.replace(window.location.search, ''),
            "origin": window.location.origin,
            "search": window.location.search,
            "params": getUrlParams()
        };

        /**
         * Set config.
         * 
         * @param  {object}  config
         * @param  {string}  config.calculationTool  The calculation tool name.
         * @param  {string}  [config.version]  The calculation tool version (optional).
         * @param  {string}  [config.build]  The calculation tool build (optional).
         * @param  {string}  [config.gtmId]  The google tag manager id (optional).
         * 
         * @memberof IframeToParentCommunication.prototype
         */
        self.config = function (config) {
            if (typeof config == 'undefined' || !config)
                throw 'Parameter config is required'

            if (typeof config.calculationTool == 'undefined' || !config.calculationTool)
                throw 'Parameter config.calculationTool is required'

            self.appConfig = Object.assign(self.appConfig, config || {})

            if (! isIframe)
                initGoogleTagManager()

            return window.communication
        }

        /**
         * Get value from config static members.
         * 
         * @param  {string}  [name]  The name of config object (optional).
         * @return {mixed}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Get calculationTool config param.
         * iframe.getConfig('calculationTool')
         * 
         * // Get all config params.
         * iframe.getConfig()
         */
        self.getConfig = function (name) {
            return typeof name != 'undefined'
                ? (self.appConfig[name] || null)
                : self.appConfig
        }

        /**
         * Get param value from location static members.
         * 
         * @param  {string}  name  The param name of location object.
         * @return {mixed}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Get iframe location href.
         * iframe.getLocation('href')
         */
        self.getLocation = function (name) {
            if (typeof name == 'undefined' || !name)
                throw 'Parameter name is required'

            return self.location[name] || null
        }

        /**
         * Get param value from iframe url search.
         *
         * @param  {string}  [name]  The param name (optional).
         * @return {mixed}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Get iframe url param.
         * iframe.getParam('userId')
         * 
         * // Get all iframe url params.
         * iframe.getParam()
         */
        self.getParam = function (name) {
            return typeof name != 'undefined'
                ? (self.location.params[name] || null)
                : self.location.params
        }

        /**
         * Send custom message to parent.
         * 
         * @param  {string}  type  The name of message type.
         * @param  {object}  data  The message data.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send custom message to parent.
         * communication.sendMessage('message-type-name', {
         *     key: 'value',
         *     ...
         * })
         */
        self.sendMessage = function (type, data) {
            if (typeof type == 'undefined' || !type)
                throw 'Parameter type is required'

            if (typeof data == 'undefined' || !data)
                throw 'Parameter data is required'

            window.parent.postMessage({
                type: type,
                data: data
            }, '*')

            sendGoogleTagManagerEvent({
                event: 'tools_message_sent',
                tools_message_type: type,
                tools_message_data: data
            })

            return window.communication
        }

        /**
         * Send resize message to parent.
         * <p style="font-size: 80%; color: red;">This method is performed automatically each time the browser window size changes.</p>
         * 
         * @param  {string}  [type]  The name of message type (optional).
         * @param  {object}  [data]  The message data (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send resize-height message to parent.
         * communication.sendResizeMessage()
         */
        self.sendResizeMessage = function (type, data) {
            if (! isIframe)
                return window.communication

            type = type || 'resize-height'

            var data = Object.assign(data || {}, {
                height: document.documentElement.offsetHeight
            })

            self.sendMessage(type, data)

            return window.communication
        }

        /**
         * Send scroll-to message to parent.
         * 
         * @param  {number}  position  The offset top position.
         * @param  {string}  [type]  The name of message type (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send scroll-to message to parent.
         * communication.sendScrollToMessage(0)
         */
        self.sendScrollToMessage = function (position, type) {
            if (! isIframe)
                return window.communication

            type = type || 'scroll-to'

            self.sendMessage(type, {
                position: position
            })

            return window.communication
        }

        /**
         * Send auth login message to parent.
         * 
         * @param  {object}  [data]  The message data (optional).
         * @param  {string}  [data.referenceBack=location.href]  The redirect iframe location href (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send auth login message to parent.
         * communication.sendAuthLoginMessage()
         */
        self.sendAuthLoginMessage = function (data) {
            if (! isIframe)
                return window.communication

            var data = Object.assign({
                referenceBack: self.getLocation('href')
            }, data || {})

            data = Object.assign(data, {
                redirect: 'login',
                calculationTool: self.appConfig.calculationTool,
                userId: self.getParam('userId'),
                profileId: self.getParam('profileId')
            })

            self.sendMessage('redirect', data)

            return window.communication
        }

        /**
         * Send auth login message to parent.
         * 
         * @param  {object}  [data]  The message data (optional).
         * @param  {string}  [data.referenceBack=location.href]  The redirect iframe location href (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send auth register message to parent.
         * communication.sendAuthRegisterMessage()
         */
        self.sendAuthRegisterMessage = function (data) {
            if (! isIframe)
                return window.communication

            var data = Object.assign({
                referenceBack: self.getLocation('href')
            }, data || {})

            data = Object.assign(data, {
                redirect: 'register',
                calculationTool: self.appConfig.calculationTool,
                userId: self.getParam('userId'),
                profileId: self.getParam('profileId')
            })

            self.sendMessage('redirect', data)

            return window.communication
        }

        /**
         * Send user action message to parent.
         * 
         * @param  {string}  action  The action name.
         * @param  {object}  [data]  The message data (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send user action message to parent.
         * communication.sendUserActionMessage('start new calculation')
         */
        self.sendUserActionMessage = function (action, data) {
            if (typeof action == 'undefined' || !action)
                throw 'Parameter action is required'

            var data = Object.assign(data || {}, {
                action: action,
                calculationTool: self.appConfig.calculationTool,
                userId: self.getParam('userId'),
                profileId: self.getParam('profileId')
            })

            self.sendMessage('user-action', data)

            return window.communication
        }

        /**
         * Send start new calculation action message to parent.
         * 
         * @param  {object}  [data]  The message data (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send start new calculation message to parent.
         * communication.sendStartNewCalculationMessage()
         */
        self.sendStartNewCalculationMessage = function (data) {
            self.sendUserActionMessage('start new calculation', data)
        }

        /**
         * Send load calculation action message to parent.
         * 
         * @param  {object}  [data]  The message data (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send load calculation message to parent.
         * communication.sendLoadCalculationMessage()
         */
        self.sendLoadCalculationMessage = function (data) {
            self.sendUserActionMessage('load calculation', data)
        }

        /**
         * Send cancel calculation action message to parent.
         * 
         * @param  {object}  [data]  The message data (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send cancel calculation message to parent.
         * communication.sendCancelCalculationMessage()
         */
        self.sendCancelCalculationMessage = function (data) {
            self.sendUserActionMessage('cancel calculation', data)
        }

        /**
         * Send finish calculation action message to parent.
         * 
         * @param  {object}  [data]  The message data (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send finish calculation message to parent.
         * communication.sendFinishCalculationMessage()
         */
        self.sendFinishCalculationMessage = function (data) {
            self.sendUserActionMessage('finish calculation', data)
        }

        /**
         * Send save calculation action message to parent.
         * 
         * @param  {object}  [data]  The message data (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send save calculation message to parent.
         * communication.sendSaveCalculationMessage()
         */
        self.sendSaveCalculationMessage = function (data) {
            self.sendUserActionMessage('save calculation', data)
        }

        /**
         * Send share calculation action message to parent.
         * 
         * @param  {object}  data  The message data.
         * @param  {string}  data.hash  The share hash.
         * @param  {string}  data.emails  The emails.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send share calculation message to parent.
         * communication.sendShareCalculationMessage()
         */
        self.sendShareCalculationMessage = function (data) {
            if (typeof data == 'undefined' || !data)
                throw 'Parameter data is required'

            if (typeof data.hash == 'undefined' || !data.hash)
                throw 'Parameter data.hash is required'

            if (typeof data.emails == 'undefined' || !data.emails)
                throw 'Parameter data.emails is required'

            var data = Object.assign(data || {}, {
                calculationTool: self.appConfig.calculationTool,
                userId: self.getParam('userId'),
                profileId: self.getParam('profileId')
            })

            self.sendUserActionMessage('share calculation', data)
        }

        /**
         * Send calculation results action message to parent.
         * 
         * @param  {object}  [data]  The message data (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send calculation results message to parent.
         * communication.sendCalculationResultsMessage()
         */
        self.sendCalculationResultsMessage = function (data) {
            self.sendUserActionMessage('send calculation results', data)
        }

        /**
         * Send download calculation results action message to parent.
         * 
         * @param  {object}  [data]  The message data (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send download calculation results message to parent.
         * communication.sendDownloadCalculationResultsMessage()
         */
        self.sendDownloadCalculationResultsMessage = function (data) {
            self.sendUserActionMessage('download calculation results', data)
        }

        /**
         * Send change step action message to parent.
         * 
         * @param  {int}  step  The step value.
         * @param  {object}  [data]  The message data (optional).
         * @param  {string}  [action='change step']  The name of message type (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send change step message to parent.
         * communication.sendChangeStepMessage(2)
         */
        self.sendChangeStepMessage = function (step, data, action) {
            if (typeof step == 'undefined' || !step)
                throw 'Parameter step is required'

            action = action || 'change step'

            var data = Object.assign(data || {}, {
                    step: step,
                    action: action,
                    userId: self.getParam('userId'),
                    profileId: self.getParam('profileId')
                })

            self.sendUserActionMessage(action, data)
        }

        /**
         * Send previous step action message to parent.
         * 
         * @param  {int}  step  The step value.
         * @param  {object}  [data]  The message data (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send start new calculation message to parent.
         * communication.sendPreviousStepMessage(1)
         */
        self.sendPreviousStepMessage = function (step, data) {
            if (typeof step == 'undefined' || !step)
                throw 'Parameter step is required'

            self.sendChangeStepMessage(step, data, 'previous step')
        }

        /**
         * Send next step action message to parent.
         * 
         * @param  {int}  step  The step value.
         * @param  {object}  [data]  The message data (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send start new calculation message to parent.
         * communication.sendNextStepMessage(3)
         */
        self.sendNextStepMessage = function (step, data) {
            if (typeof step == 'undefined' || !step)
                throw 'Parameter step is required'

            self.sendChangeStepMessage(step, data, 'next step')
        }

        /**
         * Send email message to parent.
         * 
         * @param  {object}  [data]  The message data (optional).
         * @param  {boolean}  [data.sendContactRequest]  Contact request flag (optional).
         * @param  {array}  [data.attachments]  The URLs of the attachments to be send (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send email message to parent.
         * communication.sendEmailMessage()
         */
        self.sendEmailMessage = function (data) {
            var data = Object.assign({
                attachments: [],
                sendContactRequest: false
            }, data || {})

            if (Array.isArray(data.attachments) === false)
                throw 'Parameter attachments must be array'

            data = Object.assign(data, {
                calculationTool: self.appConfig.calculationTool,
                userId: self.getParam('userId'),
                profileId: self.getParam('profileId')
            })

            self.sendMessage('email', data)

            return window.communication
        }

        /**
         * Send quotation message to parent.
         * 
         * @param  {object}  [data]  The message data (optional).
         * @param  {boolean}  [data.sendContactRequest]  Contact request flag (optional).
         * @param  {object}  [data.bom]  BOM of created project (optional).
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Send quotation message to parent.
         * communication.sendQuotationMessage()
         */
        self.sendQuotationMessage = function (data) {
            var data = Object.assign({
                bom: {},
                sendContactRequest: false
            }, data || {})

            data = Object.assign(data, {
                calculationTool: self.appConfig.calculationTool,
                userId: self.getParam('userId'),
                profileId: self.getParam('profileId')
            })

            self.sendMessage('quotation', data)

            return window.communication
        }

        /**
         * Set cookie.
         * 
         * @param  {string}  name  The cookie name.
         * @param  {object}  value  The cookie value.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Set cookie in parent.
         * communication.setCookie('cookie-name', {
         *     key: 'value',
         *     ...
         * })
         */
        self.setCookie = function (name, value) {
            if (! isIframe)
                return

            if (typeof name == 'undefined')
                throw 'Parameter name is required'

            if (typeof value == 'undefined' || !value)
                throw 'Parameter value is required'

            self.sendMessage('set-cookie', {
                name: name,
                value: value,
                calculationTool: self.appConfig.calculationTool,
                userId: self.getParam('userId'),
                profileId: self.getParam('profileId')
            })
        }

        /**
         * Get cookie.
         * 
         * @param  {string}  name  The cookie name.
         * @param  {function (value: object, name: string, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Get cookie by name from parent.
         * communication.getCookie('cookie-name', function (value, name, type) {
         *     ...
         * })
         */
        self.getCookie = function (name, callback) {
            if (! isIframe)
                return window.communication

            if (typeof name == 'undefined')
                throw 'Parameter name is required'

            if (typeof callback == 'undefined' || !callback)
                throw 'Parameter callback is required'

            var cookieCallback = function (event) {
                if (typeof event.data.type == 'undefined' || event.data.type != 'send-cookie' || typeof event.data.data == 'undefined' || typeof event.data.data.name == 'undefined' || event.data.data.name != name)
                    return

                callback(event.data.data.value, event.data.data.name, event.data.data.calculationTool, event.data.type)
                window.removeEventListener("message", cookieCallback, false)
            }
            window.addEventListener("message", cookieCallback, false)

            self.sendMessage('get-cookie', {
                name: name,
                calculationTool: self.appConfig.calculationTool,
                userId: self.getParam('userId'),
                profileId: self.getParam('profileId')
            })

            return window.communication
        }

        /**
         * Delete cookie.
         * 
         * @param  {string}  name  The cookie name.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Delete cookie in parent.
         * communication.deleteCookie('cookie-name')
         */
        self.deleteCookie = function (name) {
            if (! isIframe)
                return

            if (typeof name == 'undefined')
                throw 'Parameter name is required'

            self.sendMessage('delete-cookie', {
                name: name,
                calculationTool: self.appConfig.calculationTool,
                userId: self.getParam('userId'),
                profileId: self.getParam('profileId')
            })
        }

        /**
         * Set storage.
         * 
         * @param  {string}  name  The storage name.
         * @param  {object}  value  The storage value.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Set storage in parent.
         * communication.setStorage('storage-name', {
         *     key: 'value',
         *     ...
         * })
         */
         self.setStorage = function (name, value) {
             if (! isIframe)
                return

            if (typeof name == 'undefined')
                throw 'Parameter name is required'

            if (typeof value == 'undefined' || !value)
                throw 'Parameter value is required'

            self.sendMessage('set-storage', {
                name: name,
                value: value,
                calculationTool: self.appConfig.calculationTool,
                userId: self.getParam('userId'),
                profileId: self.getParam('profileId')
            })
        }

        /**
         * Get storage.
         * 
         * @param  {string}  name  The storage name.
         * @param  {function (value: object, name: string, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Get storage by name from parent.
         * communication.getStorage('storage-name', function (value, name, type) {
         *     ...
         * })
         */
        self.getStorage = function (name, callback) {
            if (! isIframe)
                return window.communication

            if (typeof name == 'undefined')
                throw 'Parameter name is required'

            if (typeof callback == 'undefined' || !callback)
                throw 'Parameter callback is required'

            var storageCallback = function (event) {
                if (typeof event.data.type == 'undefined' || event.data.type != 'send-storage' || typeof event.data.data == 'undefined' || typeof event.data.data.name == 'undefined' || event.data.data.name != name)
                    return

                callback(event.data.data.value, event.data.data.name, event.data.data.calculationTool, event.data.type)
                window.removeEventListener("message", storageCallback, false)
            }
            window.addEventListener("message", storageCallback, false)

            self.sendMessage('get-storage', {
                name: name,
                calculationTool: self.appConfig.calculationTool,
                userId: self.getParam('userId'),
                profileId: self.getParam('profileId')
            })

            return window.communication
        }

        /**
         * Delete storage.
         * 
         * @param  {string}  name  The storage name.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Delete storage in parent.
         * communication.deleteStorage('storage-name')
         */
        self.deleteStorage = function (name) {
            if (! isIframe)
                return

            if (typeof name == 'undefined')
                throw 'Parameter name is required'

            self.sendMessage('delete-storage', {
                name: name,
                calculationTool: self.appConfig.calculationTool,
                userId: self.getParam('userId'),
                profileId: self.getParam('profileId')
            })
        }

        /**
         * Get iframe offset.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Get iframe offset from parent.
         * communication.getOffset(function (data, type) {
         *     ...
         * })
         */
        self.getOffset = function (callback) {
            if (! isIframe)
                return window.communication

            if (typeof callback == 'undefined' || !callback)
                throw 'Parameter callback is required'

            var offsetCallback = function (event) {
                if (typeof event.data.type == 'undefined' || event.data.type != 'send-offset' || typeof event.data.data == 'undefined')
                    return

                callback(event.data.data, event.data.type)
                window.removeEventListener("message", offsetCallback, false)
            }
            window.addEventListener("message", offsetCallback, false)

            self.sendMessage('get-offset', {})

            return window.communication
        }

        /**
         * Listen custom message from parent.
         * 
         * @param  {string}  type  The name of message type.
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Listen custom message from parent.
         * communication.onMessage('message-type-name', function (data, type) {
         *     ...
         * })
         */
        self.onMessage = function (type, callback) {
            if (! isIframe)
                return window.communication

            if (typeof type == 'undefined')
                throw 'Parameter type is required'

            if (typeof callback == 'undefined' || !callback)
                throw 'Parameter callback is required'

            window.addEventListener("message", function (event) {
                if ((!type && typeof event.data.type == 'undefined') || (type && event.data.type != type))
                    return

                callback(event.data.data, event.data.type)
            }, false)

            return window.communication
        }

        /**
         * Listen all messages from parent.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Listen all messages from parent.
         * communication.onMessages(function (data, type) {
         *     ...
         * })
         */
        self.onMessages = function (callback) {
            if (! isIframe)
                return window.communication

            if (typeof callback == 'undefined' || !callback)
                throw 'Parameter callback is required'

            self.onMessage(null, callback)

            return window.communication
        }

        /**
         * Listen init message from parent.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Listen init message from parent.
         * communication.onInitMessage(function (data, type) {
         *     ...
         * })
         */
        self.onInitMessage = function (callback) {
            if (typeof callback == 'undefined' || !callback)
                throw 'Parameter callback is required'

            self.onMessage('init', callback)

            return window.communication
        }

        /**
         * Listen resize message from parent.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Listen resize message from parent.
         * communication.onResizeMessage(function (data, type) {
         *     ...
         * })
         */
        self.onResizeMessage = function (callback) {
            if (typeof callback == 'undefined' || !callback)
                throw 'Parameter callback is required'

            self.onMessage('resize-width', callback)

            return window.communication
        }

        /**
         * Listen scroll message from parent.
         * 
         * @param  {function (data: object, type: string)}  callback  The message callback function.
         * @return {instance}
         * 
         * @memberof IframeToParentCommunication.prototype
         * @example
         * // Listen scroll message from parent.
         * communication.onScrollMessage(function (data, type) {
         *     ...
         * })
         */
        self.onScrollMessage = function (callback) {
            if (typeof callback == 'undefined' || !callback)
                throw 'Parameter callback is required'

            self.onMessage('scroll', callback)

            return window.communication
        }

        /*
         * Listen event "init" from parent.
         */
        if (isIframe) {
            self.onMessage('init', function () {
                initGoogleTagManager()
                self.sendResizeMessage('ready', {
                    "calculationTool": self.appConfig.calculationTool,
                    "version": self.appConfig.version || null,
                    "build": self.appConfig.build || null,
                })
            })
        }
        
        /*
         * Listen event "resize-width" from parent.
         */
        if (isIframe) {
            self.onMessage('resize-width', function () {
                self.sendResizeMessage()
            })
        }
        
        /*
         * Listen body height change.
         */
        if (isIframe) {
            onElementHeightChange(document.body, function() {
                self.sendResizeMessage()
            })
        }

        return self
    }
    
    // Register library to iframe window
    if ((window !== window.parent) || (window.frameElement && window.frameElement.nodeName == "IFRAME")) {
        if (typeof window.communication === 'undefined')
            window.communication = communication(true)
    } else {
        window.communication = communication(false)
    }
})(window, document, HTMLIFrameElement)
